import React, { useEffect, useState } from "react";
import { StytchB2BProvider, useStytchMemberSession } from "@stytch/react/b2b";
import { StytchB2BUIClient } from "@stytch/vanilla-js/b2b";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";
import AppShellHeader from "./components/AppShellHeader";
import LibraryPage from "./components/LibraryPage";
import RubricsAndFrameworksPage from "./components/RubricsAndFrameworksPage";
import RubricEditorPage from "./components/RubricEditorPage";
import FrameworkEditorPage from "./components/FrameworkEditorPage";
import ChatView from "./components/ChatView";
import ReportCardPage from "./components/report-card/ReportCardPage";
import NotFoundPage from "./components/NotFoundPage";
import ScenarioCreatePage from "./components/ScenarioCreatePage";
import ScenarioBriefingPage from "./components/ScenarioBriefingPage";
import AuthWrapper from "./components/auth/AuthWrapper";
import Authenticate from "./components/auth/Authenticate";
import AdminOnly from "./components/auth/AdminOnly";
import ErrorBoundary from "./components/ErrorBoundary";
import { useAppData } from "./hooks/useAppData";
import { usePermissionLevel } from "./hooks/usePermissionLevel";
import AdminPage from "@/components/admin/AdminPage.tsx";
import AdminSessionPage from "./components/admin/sessions/AdminSessionPage";
import TeamPage from "./components/team/TeamPage";

const stytch = new StytchB2BUIClient(
  import.meta.env.VITE_STYTCH_PUBLIC_TOKEN || "",
);

if (!import.meta.env.VITE_STYTCH_PUBLIC_TOKEN) {
  console.error(
    "CRITICAL: VITE_STYTCH_PUBLIC_TOKEN is missing or empty. Stytch SDK will fail.",
  );
}

/**
 * Thin route wrapper around ScenarioCreatePage. Lives here (not in its own
 * file) so the routing surface stays in App.tsx where the rest of the route
 * definitions live.
 */
const ScenarioCreateRoute: React.FC<{
  getToken: () => string | undefined;
}> = ({ getToken }) => {
  const { scenarioId } = useParams<{ scenarioId?: string }>();
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { isManagerOrAbove } = usePermissionLevel();
  if (!isManagerOrAbove) {
    return <Navigate to={`/${orgSlug}/library`} replace />;
  }
  return (
    <ScenarioCreatePage
      scenarioId={scenarioId ?? null}
      getToken={getToken}
      onExitToLibrary={() => navigate(`/${orgSlug}/library`)}
    />
  );
};

const AppContent: React.FC = () => {
  const {
    sessions,
    activeSessionId,
    sessionNotFound,
    messages,
    scenario,
    isLoadingMessages,
    error,
    activePersonaId,
    setActivePersonaId,
    revealedDocuments,
    currentInput,
    setCurrentInput,
    isLoading,
    handleLogout,
    handleNewSession,
    handleBeginScenario,
    handleResumeScenario,
    handleRenameSession,
    handleExportChat,
    handleDeleteSession,
    handleSendMessage,
    activePersona,
  } = useAppData(stytch);

  // The documents overlay is owned here because the header's button toggles it
  // and ChatView renders it — it is the one piece of chat chrome that outlived
  // the tool column.
  const [documentsOpen, setDocumentsOpen] = useState(false);

  // The overlay lives inside ChatView but its flag lives here, so leaving the
  // chat route would otherwise strand it open with no control able to close it.
  useEffect(() => {
    if (!activeSessionId) setDocumentsOpen(false);
  }, [activeSessionId]);

  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const getToken = () => stytch.session.getTokens()?.session_token;

  if (error) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background text-error flex-col gap-4">
        <span className="material-symbols-outlined text-5xl">warning</span>
        <div className="text-xl font-bold font-headline">Error Loading Application</div>
        <div className="text-slate-400 font-body">{error}</div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-surface-container hover:bg-surface-container-high rounded text-on-surface transition-colors font-label text-sm uppercase tracking-wider"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen w-full bg-background text-on-surface overflow-hidden font-body">
      <AppShellHeader
        activeScenarioId={activeSessionId ? scenario?.id : undefined}
        activeScenarioTitle={activeSessionId ? scenario?.title : undefined}
        documentCount={revealedDocuments.length}
        onToggleDocuments={activeSessionId ? () => setDocumentsOpen((o) => !o) : undefined}
        documentsOpen={documentsOpen}
        onLogout={handleLogout}
        getToken={getToken}
      />
      <div className="flex flex-1 min-h-0 w-full">
      <Routes>
        <Route
          path="library"
          element={
            <LibraryPage
              sessions={sessions}
              getToken={getToken}
              // The briefing page is both the scenario's info page and the
              // gate in front of a first session: the card body opens it for
              // any scenario, and its own CTA creates the session. The card's
              // CTA still resumes straight to chat, so the common path back
              // into a scenario in progress stays one click.
              onOpenBriefing={(id) => {
                navigate(`/${orgSlug}/scenarios/${id}`);
              }}
              onResumeScenario={handleResumeScenario}
              onOpenCreate={() => navigate(`/${orgSlug}/scenarios/new`)}
              onResumeDraft={(id) =>
                navigate(`/${orgSlug}/scenarios/${id}/edit`)
              }
              onEditScenario={(id) =>
                navigate(`/${orgSlug}/scenarios/${id}/edit`)
              }
            />
          }
        />
        <Route
          path="scenarios/new"
          element={<ScenarioCreateRoute getToken={getToken} />}
        />
        <Route
          path="scenarios/:scenarioId/edit"
          element={<ScenarioCreateRoute getToken={getToken} />}
        />
        {/* Listed last of the three for readability. React Router ranks a
            static segment above a dynamic one regardless of source order, so
            "scenarios/new" still reaches the editor rather than this page
            trying to fetch a scenario called "new". */}
        <Route
          path="scenarios/:scenarioId"
          element={
            <ScenarioBriefingPage
              getToken={getToken}
              onBeginScenario={handleBeginScenario}
              sessions={sessions}
              onResumeScenario={handleResumeScenario}
            />
          }
        />
        <Route
          path="chat/:sessionId"
          element={
            sessionNotFound ? (
              <NotFoundPage />
            ) : (
              <ChatView
                scenario={scenario}
                activePersona={activePersona}
                activePersonaId={activePersonaId}
                setActivePersonaId={setActivePersonaId}
                activeSessionId={activeSessionId}
                messages={messages}
                isLoadingMessages={isLoadingMessages}
                revealedDocuments={revealedDocuments}
                currentInput={currentInput}
                setCurrentInput={setCurrentInput}
                isLoading={isLoading}
                onSendMessage={handleSendMessage}
                onExportChat={handleExportChat}
                documentsOpen={documentsOpen}
                onDocumentsOpenChange={setDocumentsOpen}
                onNewSession={handleNewSession}
                onRenameSession={handleRenameSession}
                onDeleteSession={handleDeleteSession}
                sessions={sessions}
                getToken={getToken}
              />
            )
          }
        />
        <Route
          path="sessions/:sessionId/report-card"
          element={<ReportCardPage getToken={getToken} />}
        />
        <Route
          path="admin"
          element={
            <AdminPage getToken={() => stytch.session.getTokens()?.session_token} />
          }
        />
        <Route
          path="admin/sessions/:sessionId"
          element={
            <AdminSessionPage getToken={() => stytch.session.getTokens()?.session_token} />
          }
        />
        <Route
          path="team"
          element={
            <TeamPage getToken={() => stytch.session.getTokens()?.session_token} />
          }
        />
        <Route
          path="rubrics"
          element={
            <AdminOnly minLevel="manager">
              <RubricsAndFrameworksPage getToken={getToken} />
            </AdminOnly>
          }
        />
        <Route
          path="rubrics/new"
          element={
            <AdminOnly minLevel="manager">
              <RubricEditorPage getToken={getToken} />
            </AdminOnly>
          }
        />
        <Route
          path="rubrics/:rubricId/edit"
          element={
            <AdminOnly minLevel="manager">
              <RubricEditorPage getToken={getToken} />
            </AdminOnly>
          }
        />
        <Route
          path="frameworks/new"
          element={
            <AdminOnly minLevel="manager">
              <FrameworkEditorPage getToken={getToken} />
            </AdminOnly>
          }
        />
        <Route
          path="frameworks/:frameworkId/edit"
          element={
            <AdminOnly minLevel="manager">
              <FrameworkEditorPage getToken={getToken} />
            </AdminOnly>
          }
        />
        <Route path="*" element={<Navigate to="library" replace />} />
      </Routes>
      </div>
    </div>
  );
};

const RootRedirect: React.FC = () => {
  const { session } = useStytchMemberSession();
  return <Navigate to={`/${session!.organization_slug}/library`} replace />;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <StytchB2BProvider stytch={stytch}>
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              element={
                <AuthWrapper>
                  <RootRedirect />
                </AuthWrapper>
              }
            />
            <Route path="/authenticate" element={<Authenticate />} />
            <Route
              path="/:orgSlug/*"
              element={
                <AuthWrapper>
                  <AppContent />
                </AuthWrapper>
              }
            />
          </Routes>
        </BrowserRouter>
      </StytchB2BProvider>
    </ErrorBoundary>
  );
};

export default App;
